import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase/config";
import { QuestionBank } from "../types";
import { getSectionById } from "./sectionService";

const COLLECTION = "questionBanks";

type QuestionBankDB = Omit<QuestionBank, 'id'>;

export const getQuestionBanks = async (sectionId?: string): Promise<QuestionBank[]> => {
  try {
    let constraints = [];
    if (sectionId) {
      constraints.push(where("sectionId", "==", sectionId));
    }
    constraints.push(orderBy("createdAt", "desc"));
    
    const q = query(collection(db, COLLECTION), ...constraints);
    const snapshot = await getDocs(q);
    
    const banks = await Promise.all(snapshot.docs.map(async doc => {
      const data = doc.data();
      let sectionDetails = null;
      if (data.sectionId) {
        sectionDetails = await getSectionById(data.sectionId);
      }
      return {
        id: doc.id,
        ...data,
        sectionName: sectionDetails?.name || data.sectionName || "",
        sectionType: sectionDetails?.sectionType || data.sectionType || "",
        sectionDuration: sectionDetails?.duration || 0,
        sectionQuestionCount: sectionDetails?.questionCount || 0,
      } as QuestionBank;
    }));
    
    return banks;
  } catch (error) {
    console.error("Error fetching question banks:", error);
    throw error;
  }
};

export const getQuestionBankById = async (id: string): Promise<QuestionBank | null> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      let sectionDetails = null;
      if (data.sectionId) {
        sectionDetails = await getSectionById(data.sectionId);
      }
      return {
        id: docSnap.id,
        ...data,
        sectionName: sectionDetails?.name || data.sectionName || "",
        sectionType: sectionDetails?.sectionType || data.sectionType || "",
        sectionDuration: sectionDetails?.duration || 0,
        sectionQuestionCount: sectionDetails?.questionCount || 0,
      } as QuestionBank;
    }
    return null;
  } catch (error) {
    console.error("Error fetching question bank:", error);
    throw error;
  }
};

export const createQuestionBank = async (bankData: Omit<QuestionBank, "id" | "createdAt" | "updatedAt">, userId: string): Promise<string> => {
  try {
    const sectionDetails = await getSectionById(bankData.sectionId);
    
    const now = Timestamp.now();
    const payload: QuestionBankDB = {
      ...bankData,
      sectionName: sectionDetails?.name || "",
      sectionType: sectionDetails?.sectionType || "",
      createdBy: userId,
      createdAt: now.toDate().toISOString(),
      updatedAt: now.toDate().toISOString(),
    };
    
    const docRef = await addDoc(collection(db, COLLECTION), payload);
    return docRef.id;
  } catch (error) {
    console.error("Error creating question bank:", error);
    throw error;
  }
};

export const updateQuestionBank = async (id: string, bankData: Partial<Omit<QuestionBank, "id" | "createdAt" | "updatedAt">>): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    let updateData: any = { ...bankData };
    
    if (bankData.sectionId) {
      const sectionDetails = await getSectionById(bankData.sectionId);
      updateData.sectionName = sectionDetails?.name || "";
      updateData.sectionType = sectionDetails?.sectionType || "";
    }
    
    updateData.updatedAt = Timestamp.now().toDate().toISOString();
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating question bank:", error);
    throw error;
  }
}; 

export const deleteQuestionBank = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting question bank:", error);
    throw error;
  }
};

export const getBanksBySection = async (sectionId: string): Promise<QuestionBank[]> => {
  return getQuestionBanks(sectionId);
};